import * as authApi from '~/modules/auth/api/auth.api';
import { useCurrentEmployeeStore } from '~/modules/employees/stores/current-employee.store';
import { BadRequestApiException } from '~/shared/errors/ApiException';

/**
 * Сессия живёт в httpOnly-куке и из JS не видна, поэтому признаком
 * авторизации служит наличие профиля, а её потерей — ответ 401.
 */
export const useAuthStore = defineStore('auth', () => {
  const currentEmployeeStore = useCurrentEmployeeStore();

  let sessionInTransition = false;

  const isLoggedIn = computed(() => !!currentEmployeeStore.currentEmployee);

  async function logout() {
    sessionInTransition = true;
    try {
      await authApi.logout();
    } catch {
      /* сессия уже могла быть погашена на бэкенде */
    } finally {
      sessionInTransition = false;
    }

    currentEmployeeStore.dropEmployee();
  }

  /** @throws {ApiException} */
  async function authorize(authorizationCode: string) {
    sessionInTransition = true;
    try {
      await authApi.confirmLimsAuth(authorizationCode);
      await currentEmployeeStore.loadEmployee();
    } catch (error) {
      if (!(error instanceof BadRequestApiException)) {
        throw error;
      }
      useToast().error('Авторизация недоступна');
      console.error(error);
      throw error;
    } finally {
      sessionInTransition = false;
    }
  }

  /**
   * Запросы самой авторизации и выхода реакции не вызывают: 401 там ожидаем
   * и потери сессии не означает.
   */
  async function handleSessionLost() {
    if (sessionInTransition || !isLoggedIn.value) return;

    currentEmployeeStore.dropEmployee();
    await navigateTo('/auth/not-authorized');
  }

  /**
   * Ссылка на авторизацию через лимс
   * TODO: в перспективе хотим получать это значение с бэка
   */
  function getSignInUrl(backUrl: string) {
    const url = useRuntimeConfig().public.lims.signInUrl;

    return `${url}?backUrl=${backUrl}`;
  }

  return {
    isLoggedIn,
    logout,
    authorize,
    handleSessionLost,
    getSignInUrl,
  };
});
